import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { toCreasedNormals } from 'three/addons/utils/BufferGeometryUtils.js';

/* Jay Cases — scroll-driven 3D hero.
 * One instance per section root (.jay-hero). Reads config from data-* attrs
 * so all copy + the Arctic product link stay editable in the theme customizer. */

function initHero(root) {
  if (!root || root.dataset.jayInit === '1') return;
  root.dataset.jayInit = '1';

  const HALF_PI = Math.PI / 2;
  const cfg = {
    modelUrl: root.dataset.modelUrl,
    stickerUrl: root.dataset.stickerUrl || '',
    bg: root.dataset.bg || '#05070a',
    colors: {
      green: parseInt((root.dataset.colorGreen || '#14c2ad').slice(1), 16),
      white: parseInt((root.dataset.colorWhite || '#eef0f2').slice(1), 16),
      black: parseInt((root.dataset.colorBlack || '#181c22').slice(1), 16),
      blue:  parseInt((root.dataset.colorBlue  || '#2f74e8').slice(1), 16),
    },
    // rig pivots (case-local, from the model)
    lidPivot:   { x: 0.66, y: 0.17, z: 0 },   // rear hinge, axis Z
    latchPivot: { x: -0.645, y: 0.282, z: 0.51 },// on the lid rail axis so the clasp wraps it (no clip)
    // sticker decal — fills the valley (1mm gap to each ridge), bottom edge fixed at the front curve
    sticker: { x: -0.275, y: 0.415, z: 0.51, size: 0.45, rotZ: -HALF_PI }, // size = image height; width = size*aspect (undistorted)
    lidOpenRad: -2.15, latchOpenRad: 0.62,
    // presentation poses (per-case rotation)
    rowPose:   { x: HALF_PI, y: HALF_PI },     // tipped onto hatch face; lid top (sticker) faces camera
    frontPose: { x: 0.05,  y: HALF_PI + 0.5 },// front face w/ latches toward camera (beat 1)
    caseScale: 0.9, rowGap: 1.9,
  };
  const ORDER = ['green', 'white', 'black', 'blue'];
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const stage = root.querySelector('.jay-hero__stage');
  const canvas = root.querySelector('.jay-hero__canvas');
  const scroller = root.querySelector('.jay-hero__scroll');

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const sceneRoot = new THREE.Group(); scene.add(sceneRoot); // scaled down on narrow screens so cases always fit
  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(0, 1.05, 7.4);
  camera.lookAt(0, -0.05, 0);

  // soft studio lighting for a clean matte look
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.5).texture;
  scene.add(new THREE.HemisphereLight(0xffffff, 0x2a2f38, 0.7));
  const key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(2.5, 4.5, 3.5); scene.add(key);
  const fill = new THREE.DirectionalLight(0xdfe8ff, 0.5); fill.position.set(-3.5, 1.0, 2.0); scene.add(fill);
  const rim = new THREE.DirectionalLight(0xbcd2ff, 0.9); rim.position.set(-1.5, 2.5, -4.0); scene.add(rim);

  // ---------- Aurora borealis background (full-screen shader, parallax by scroll) ----------
  const bgScene = new THREE.Scene();
  const bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const auroraMat = new THREE.ShaderMaterial({
    depthTest: false, depthWrite: false,
    uniforms: { uTime: { value: 0 }, uScroll: { value: 0 }, uAspect: { value: 1 } },
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
    fragmentShader: `
      precision highp float;
      uniform float uTime, uScroll, uAspect; varying vec2 vUv;
      float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
      float noise(vec2 p){ vec2 i=floor(p), f=fract(p);
        float a=hash(i), b=hash(i+vec2(1,0)), c=hash(i+vec2(0,1)), d=hash(i+vec2(1,1));
        vec2 u=f*f*(3.0-2.0*f); return mix(mix(a,b,u.x),mix(c,d,u.x),u.y); }
      float fbm(vec2 p){ float v=0.0, a=0.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.02; a*=0.5; } return v; }
      // one layer of randomly-placed, randomly-sized stars (cell-jittered, not a grid)
      float starLayer(vec2 uv, float density, float thresh, float tw){
        vec2 g = uv*density; vec2 cell = floor(g); vec2 f = fract(g);
        float present = hash(cell*1.7);
        if (present < thresh) return 0.0;
        vec2 pos = vec2(hash(cell+3.1), hash(cell+7.9));       // random position in cell
        float bri = hash(cell+11.3);                            // random brightness
        float size = mix(0.006, 0.045, bri*bri);               // random size (few big, many small)
        float d = length((f - pos));
        float s = smoothstep(size, 0.0, d);
        float twk = 0.65 + 0.35*sin(tw + bri*50.0);            // gentle twinkle
        return s * (0.25 + 0.75*bri) * twk;
      }
      void main(){
        vec2 uv = vUv; vec2 p = (uv-0.5); p.x *= uAspect;
        float t = uTime*0.03;
        vec2 flow = vec2(t*0.6 + uScroll*0.12, -uScroll*0.35 - t*0.25);   // slow drift + gentle parallax
        // deep-space gradient
        vec3 col = mix(vec3(0.015,0.03,0.06), vec3(0.005,0.008,0.02), uv.y);
        // starfield: several depth layers, each parallax-scrolls a little (nearer = more)
        vec2 su = uv * vec2(uAspect, 1.0);
        float stars = 0.0;
        stars += starLayer(su + vec2(0.0, uScroll*0.02), 42.0,  0.55, uTime*1.2);   // far, tiny, dense
        stars += starLayer(su + vec2(0.0, uScroll*0.045), 24.0, 0.6,  uTime*1.6) * 1.2; // mid
        stars += starLayer(su + vec2(0.0, uScroll*0.075), 13.0, 0.72, uTime*2.2) * 1.5; // near, bigger, sparse
        col += vec3(0.92,0.95,1.0) * stars;
        // aurora curtains (blurry fbm)
        vec2 ap = p*vec2(2.2,1.1) + flow;
        float bands = fbm(ap + vec2(fbm(ap*0.5 + t),0.0));
        float curtain = fbm(vec2(p.x*3.0 + t*0.5 + bands*1.6, p.y*1.4 + t*0.1));
        float aurora = smoothstep(0.30,0.85,bands) * smoothstep(0.15,0.80,curtain);
        float region = smoothstep(0.0,0.45,uv.y) * (1.0 - smoothstep(0.6,1.05,uv.y));
        aurora *= region;
        // green -> teal -> purple -> blue
        float hue = fbm(p*1.4 + flow*0.5);
        vec3 ac = mix(vec3(0.12,1.0,0.55), vec3(0.10,0.75,0.9), smoothstep(0.2,0.5,hue));
        ac = mix(ac, vec3(0.6,0.25,1.0), smoothstep(0.5,0.68,hue));
        ac = mix(ac, vec3(0.25,0.5,1.0),  smoothstep(0.68,0.9,hue));
        col += ac * aurora * 1.15;
        col *= smoothstep(1.35,0.35,length(uv-0.5));   // vignette
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
  bgScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), auroraMat));

  // ---- Pelican pick-and-pluck foam texture: charcoal with visible cells/bubbles ----
  // aspect-correct canvas (matches the foam 0.95 x 1.78) so bubbles stay round and cut cells stay square
  function foamCanvas(withGrid) {
    const W = 512, H = 958, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const g = cv.getContext('2d');
    g.fillStyle = '#3a3d43'; g.fillRect(0, 0, W, H);
    for (let i = 0; i < 46000; i++) {                  // dense open-cell bubbles (this is the texture used on both foams)
      const x = Math.random() * W, y = Math.random() * H, r = 1.4 + Math.random() * 5.5;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, 'rgba(8,9,11,0.9)');
      grd.addColorStop(0.7, 'rgba(42,44,49,0.4)');
      grd.addColorStop(1, 'rgba(120,124,132,0.45)');
      g.fillStyle = grd; g.beginPath(); g.arc(x, y, r, 0, 6.2832); g.fill();
    }
    if (withGrid) {                                    // pick-and-pluck cut grid, inset with a rim, barely visible (base only)
      const rim = 58, nU = 6, nV = 11, x0 = rim, x1 = W - rim, y0 = rim, y1 = H - rim;
      g.strokeStyle = 'rgba(8,9,11,0.5)'; g.lineWidth = 2.5;
      for (let i = 0; i <= nU; i++) { const x = x0 + (x1 - x0) * i / nU; g.beginPath(); g.moveTo(x, y0); g.lineTo(x, y1); g.stroke(); }
      for (let i = 0; i <= nV; i++) { const y = y0 + (y1 - y0) * i / nV; g.beginPath(); g.moveTo(x0, y); g.lineTo(x1, y); g.stroke(); }
    }
    return cv;
  }
  const FOAM_CUT = foamCanvas(true), FOAM_PLAIN = foamCanvas(false);
  function foamTex(cv, srgb) { const t = new THREE.CanvasTexture(cv); t.anisotropy = 4; if (srgb) t.colorSpace = THREE.SRGBColorSpace; return t; }
  function foamMat(withGrid) {
    const cv = withGrid ? FOAM_CUT : FOAM_PLAIN;
    return new THREE.MeshStandardMaterial({
      map: foamTex(cv, true), bumpMap: foamTex(cv, false), bumpScale: 0.03,
      roughness: 0.96, metalness: 0, color: 0xcfd2d8,
    });
  }
  // planar 0-1 UVs across the top face so a single (non-tiled) foam texture maps with a clean rim
  function planarUV(geo) {
    geo.computeBoundingBox(); const b = geo.boundingBox, pos = geo.attributes.position, n = pos.count, uv = new Float32Array(n * 2);
    const dx = (b.max.x - b.min.x) || 1, dz = (b.max.z - b.min.z) || 1;
    for (let i = 0; i < n; i++) { uv[i * 2] = (pos.getX(i) - b.min.x) / dx; uv[i * 2 + 1] = (pos.getZ(i) - b.min.z) / dz; }
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); return geo;
  }
  // rounded-corner slab so the foam matches the case's rounded cavity and never
  // clips through the rigid shell. w=X, d=Z, t=Y thickness, r=corner radius.
  function roundedSlab(w, d, t, r) {
    const hw = w / 2, hd = d / 2, sh = new THREE.Shape();
    sh.moveTo(-hw + r, -hd);
    sh.lineTo(hw - r, -hd); sh.quadraticCurveTo(hw, -hd, hw, -hd + r);
    sh.lineTo(hw, hd - r); sh.quadraticCurveTo(hw, hd, hw - r, hd);
    sh.lineTo(-hw + r, hd); sh.quadraticCurveTo(-hw, hd, -hw, hd - r);
    sh.lineTo(-hw, -hd + r); sh.quadraticCurveTo(-hw, -hd, -hw + r, -hd);
    const geo = new THREE.ExtrudeGeometry(sh, { depth: t, bevelEnabled: false, curveSegments: 5 });
    geo.rotateX(-Math.PI / 2); geo.center();
    return geo;
  }
  // taper a slab's +Y end inward (ratio<1) so foam matches a bowl that narrows toward its ceiling
  function taperGeo(geo, ratioTop) {
    const pos = geo.attributes.position; geo.computeBoundingBox();
    const y0 = geo.boundingBox.min.y, y1 = geo.boundingBox.max.y;
    for (let i = 0; i < pos.count; i++) {
      const f = (pos.getY(i) - y0) / (y1 - y0);         // 0 bottom -> 1 top
      const s = 1 + f * (ratioTop - 1);
      pos.setX(i, pos.getX(i) * s); pos.setZ(i, pos.getZ(i) * s);
    }
    pos.needsUpdate = true; geo.computeVertexNormals(); return geo;
  }

  let PARTS = null;
  let STICKER_TEX = null, STICKER_ASPECT = 0.667;
  const cases = {};

  function buildCase(colorName) {
    // soft matte injection-molded ABS (higher roughness + low reflection diffuses surface striping)
    const shell = new THREE.MeshPhysicalMaterial({
      color: cfg.colors[colorName], roughness: 0.78, metalness: 0.0,
      clearcoat: 0.05, clearcoatRoughness: 0.7, envMapIntensity: 0.28,
      sheen: 0.08, sheenRoughness: 0.9, sheenColor: 0xffffff,
    });
    const g = new THREE.Group();
    const base = PARTS.base.clone(); base.material = shell; g.add(base);

    // lid on rear-hinge pivot
    const lidPivot = new THREE.Group();
    lidPivot.position.set(cfg.lidPivot.x, cfg.lidPivot.y, cfg.lidPivot.z);
    const lid = PARTS.lid.clone(); lid.material = shell;
    lid.position.set(-cfg.lidPivot.x, -cfg.lidPivot.y, -cfg.lidPivot.z);
    lidPivot.add(lid); g.add(lidPivot);

    // latches: hinged to the LID rails, so they lift with the lid when it opens
    const latchPivots = [];
    [['latch1', -1], ['latch2', 1]].forEach(([key, sign]) => {
      const p = new THREE.Group();
      p.position.set(cfg.latchPivot.x - cfg.lidPivot.x,
                     cfg.latchPivot.y - cfg.lidPivot.y,
                     sign * cfg.latchPivot.z - cfg.lidPivot.z);
      const mesh = PARTS[key].clone(); mesh.material = shell;
      mesh.position.set(-cfg.latchPivot.x, -cfg.latchPivot.y, -sign * cfg.latchPivot.z);
      p.add(mesh); lidPivot.add(p); latchPivots.push(p);
    });

    // base foam: wall-to-wall, raised so the top meets the opening's 90° edge (cavity X[-0.51,0.45], Z ±0.90)
    const baseFoam = new THREE.Mesh(planarUV(roundedSlab(0.95, 1.78, 0.56, 0.1)), foamMat(true));
    baseFoam.position.set(-0.03, -0.09, 0); g.add(baseFoam); // top ~0.19 (rim), bottom ~-0.37 (floor)
    // lid foam: tapered slab filling the bowl (wide at opening -> narrower at ceiling), thicker; UNCUT (no grid)
    const lidFoam = new THREE.Mesh(taperGeo(planarUV(roundedSlab(0.95, 1.78, 0.16, 0.1)), 0.84), foamMat(false));
    lidFoam.position.set(-0.69, 0.13, 0); // lid-local -> model (-0.03, 0.30, 0), fills opening(0.22)->ceiling(0.38)
    lidPivot.add(lidFoam);

    // sticker decal on the lid top (child of lid so it opens with it)
    if (cfg.stickerUrl && STICKER_TEX) {
      const s = cfg.sticker, w = s.size * STICKER_ASPECT, h = s.size;
      const decal = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),   // undistorted (image aspect preserved)
        new THREE.MeshBasicMaterial({ map: STICKER_TEX, transparent: true, toneMapped: false,
          polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 }));
      decal.rotation.set(-HALF_PI, 0, s.rotZ);   // lie flat, facing +Y
      decal.position.set(s.x - cfg.lidPivot.x, s.y - cfg.lidPivot.y, s.z - cfg.lidPivot.z);
      lidPivot.add(decal);
    }

    g.rotation.set(cfg.rowPose.x, cfg.rowPose.y, 0);
    g.scale.setScalar(cfg.caseScale);
    return {
      root: g, lidPivot, latchPivots,
      setLid: t => { lidPivot.rotation.z = t * cfg.lidOpenRad; },
      setLatch: t => latchPivots.forEach(p => { p.rotation.z = -t * cfg.latchOpenRad; }),
    };
  }

  function buildTimeline() {
    const { green, white, black, blue } = cases;
    const q = s => root.querySelector(s);
    const rp = cfg.rowPose, fp = cfg.frontPose;

    if (reduce) {
      root.querySelectorAll('.jay-hero__cap').forEach(c => (c.style.opacity = 1));
      q('#jay-cap1').style.opacity = 0; q('#jay-cap2').style.opacity = 0;
      black.root.position.set(9, 4, 0);
      white.root.position.set(0, -6, 0);
      green.root.position.set(-1.55, 0, 0); green.root.rotation.set(fp.x, fp.y - 0.85, 0); green.root.scale.setScalar(0.7);
      blue.root.position.set(1.55, 0, 0);  blue.root.rotation.set(fp.x, fp.y - 0.15, 0);  blue.root.scale.setScalar(0.7);
      render(); return;
    }

    const tl = gsap.timeline({ paused: true });

    // ---- BEAT 1: cases tumble away like they were dropped; black centers, turns to face us ----
    tl.addLabel('b1');
    // black turns to face us and grows in place on the right, THEN slides to
    // center once the others have fallen clear (avoids clipping through white).
    tl.to(black.root.rotation, { x: fp.x, y: fp.y, z: 0, duration: 1, ease: 'power2.out' }, 'b1');
    tl.to(black.root.scale, { x: 1.32, y: 1.32, z: 1.32, duration: 1 }, 'b1');
    tl.to(black.root.position, { x: -0.35, y: -0.02, duration: 0.55, ease: 'power2.inOut' }, 'b1+=0.5');
    // others tumble away at a steady (linear) speed so they don't fly off — matches the black case's pace.
    // fall far enough that they clear even a tall phone viewport (the scene scales down on mobile).
    const TUMB = 2.6;
    tl.to(green.root.position, { x: '-=3.6', y: '-=8.5', duration: TUMB, ease: 'none' }, 'b1');
    tl.to(green.root.rotation, { x: '+=4.6', y: '-=2.8', z: '+=6.0', duration: TUMB, ease: 'none' }, 'b1');
    tl.to(blue.root.position, { x: '+=3.7', y: '-=8.5', duration: TUMB, ease: 'none' }, 'b1');
    tl.to(blue.root.rotation, { x: '+=5.8', y: '+=3.3', z: '-=5.0', duration: TUMB, ease: 'none' }, 'b1');
    tl.to(white.root.position, { x: '-=0.9', y: '-=8.5', duration: TUMB, ease: 'none' }, 'b1');
    tl.to(white.root.rotation, { x: '+=6.6', z: '-=5.4', duration: TUMB, ease: 'none' }, 'b1');
    tl.fromTo(q('#jay-cap1'), { opacity: 0, x: 60 }, { opacity: 1, x: 0, duration: 0.5 }, 'b1+=0.95');

    // ---- BEAT 2: black exits; white returns, latches release + lid opens to show foam ----
    tl.addLabel('b2', '>');
    tl.to(q('#jay-cap1'), { opacity: 0, x: 60, duration: 0.35 }, 'b2');
    tl.to(black.root.position, { x: 10, y: 5.5, duration: 1.4, ease: 'none' }, 'b2');
    tl.to(black.root.rotation, { z: '-=2.5', x: '+=2', duration: 1.4, ease: 'none' }, 'b2');
    tl.fromTo(white.root.position, { x: -1.6, y: -8.5 }, { x: 0.1, y: -0.12, duration: 1.4, ease: 'power2.out', immediateRender: false }, 'b2');
    tl.fromTo(white.root.rotation,
      { x: rp.x, y: rp.y, z: 0 },
      { x: 0.5, y: HALF_PI + 0.32, z: 0, duration: 1.4, ease: 'power2.out', immediateRender: false }, 'b2');
    tl.to(white.root.scale, { x: 1.15, y: 1.15, z: 1.15, duration: 1.4 }, 'b2');
    tl.to(white, { onUpdate() { white.setLatch(this.progress()); }, duration: 0.5 }, 'b2+=0.7');  // unclip first
    tl.to(white, { onUpdate() { white.setLid(this.progress()); }, duration: 0.85 }, 'b2+=1.05');   // then open
    tl.fromTo(q('#jay-cap2'), { opacity: 0, x: -60 }, { opacity: 1, x: 0, duration: 0.5 }, 'b2+=1.55');

    // ---- BEAT 3: white exits; green + blue slide in close, framed by centered copy ----
    tl.addLabel('b3', '>');
    tl.to(q('#jay-cap2'), { opacity: 0, x: -60, duration: 0.35 }, 'b3');
    tl.to(white.root.position, { y: -9.5, duration: 1.5, ease: 'none' }, 'b3');   // fully off-screen on tall phones too
    tl.to(white.root.rotation, { x: '+=2.2', duration: 1.5, ease: 'none' }, 'b3');
    tl.fromTo(green.root.position, { x: -5, y: 0 }, { x: -1.42, y: -0.02, duration: 1, ease: 'power2.out', immediateRender: false }, 'b3');
    tl.fromTo(blue.root.position, { x: 5, y: 0 }, { x: 1.42, y: -0.02, duration: 1, ease: 'power2.out', immediateRender: false }, 'b3');
    tl.to(green.root.rotation, { x: fp.x, y: fp.y - 0.9, z: 0, duration: 1 }, 'b3');
    tl.to(blue.root.rotation, { x: fp.x, y: fp.y - 0.1, z: 0, duration: 1 }, 'b3');
    tl.to([green.root.scale, blue.root.scale], { x: 0.92, y: 0.92, z: 0.92, duration: 1 }, 'b3');
    tl.fromTo(q('#jay-cap3'), { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.5 }, 'b3+=0.55');

    // --- scroll driver (native CSS sticky pins the stage; renders on demand, no rAF needed) ---
    let targetP = 0, currentP = -1, smoothing = false, frozen = false;
    function computeProgress() {
      const total = scroller.offsetHeight - stage.offsetHeight;
      if (total <= 0) return 0;
      return Math.min(1, Math.max(0, -scroller.getBoundingClientRect().top / total));
    }
    function draw(p) { if (p === currentP) return; currentP = p; tl.progress(p); auroraMat.uniforms.uScroll.value = p; render(); }
    function smoothStep() {
      if (frozen) { smoothing = false; return; }
      if (Math.abs(targetP - currentP) < 0.0005) { smoothing = false; draw(targetP); return; }
      draw(currentP + (targetP - currentP) * 0.2);
      requestAnimationFrame(smoothStep);
    }
    function onScroll() {
      if (frozen) return;
      targetP = computeProgress(); draw(targetP);
      if (!smoothing) { smoothing = true; requestAnimationFrame(smoothStep); }
    }
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', () => { onResize(); currentP = -1; onScroll(); });
    addEventListener('load', onScroll);
    draw(0); onScroll();
    if (new URLSearchParams(location.search).has('jaydebug')) {
      window.__jayFreeze = (p) => { frozen = true; currentP = -1; tl.progress(p); render(); };
      window.__jayUnfreeze = () => { frozen = false; currentP = -1; onScroll(); };
    }
  }

  function onResize() {
    const w = stage.clientWidth || innerWidth, h = stage.clientHeight || innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
    // keep the horizontal spread on screen on narrow/portrait viewports
    const s = Math.max(0.42, Math.min(1, 0.72 * camera.aspect));
    sceneRoot.scale.setScalar(s);
    render();
  }
  addEventListener('resize', onResize);

  function render() {
    auroraMat.uniforms.uAspect.value = camera.aspect;
    renderer.autoClear = false;
    renderer.clear();
    renderer.render(bgScene, bgCam);   // aurora backdrop
    renderer.render(scene, camera);    // cases
  }

  // slow aurora drift (only while the hero is on screen; independent of the scroll-driven renders)
  let drifting = false, lastDrift = 0;
  function drift(now) { if (!drifting) return; requestAnimationFrame(drift);
    if (now - lastDrift < 40) return;   // ~25fps is plenty for a slow drift
    lastDrift = now; auroraMat.uniforms.uTime.value = now / 1000; render(); }
  new IntersectionObserver((es) => {
    const vis = es[0].isIntersecting;
    if (vis && !drifting) { drifting = true; requestAnimationFrame(drift); }
    else if (!vis) drifting = false;
  }, { threshold: 0 }).observe(stage);

  function loadSticker() {
    return new Promise((resolve) => {
      if (!cfg.stickerUrl) return resolve();
      new THREE.TextureLoader().load(cfg.stickerUrl, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
        if (tex.image && tex.image.width) STICKER_ASPECT = tex.image.width / tex.image.height;
        STICKER_TEX = tex; resolve();
      }, undefined, () => { console.warn('[jay-hero] sticker failed to load:', cfg.stickerUrl); resolve(); });
    });
  }

  new GLTFLoader().load(cfg.modelUrl, (gltf) => {
    const byName = {}; gltf.scene.traverse(o => { if (o.isMesh) byName[o.name] = o; });
    PARTS = { base: byName.base, lid: byName.lid, latch1: byName.latch1, latch2: byName.latch2 };
    // creased normals keep the real 90° edges; slightly higher angle softens shallow facets (less striping)
    for (const k in PARTS) { PARTS[k].geometry = toCreasedNormals(PARTS[k].geometry, Math.PI / 2.4); }
    loadSticker().then(() => {
      ORDER.forEach((name, i) => {
        const c = buildCase(name);
        c.root.position.set((i - (ORDER.length - 1) / 2) * cfg.rowGap, 0, 0);
        sceneRoot.add(c.root); cases[name] = c;
      });
      onResize(); buildTimeline();
      if (new URLSearchParams(location.search).has('jaydebug')) window.__jay = { scene, camera, cases, renderer, render, cfg, THREE, sceneRoot };
    });
  }, undefined, (err) => console.error('[jay-hero] model failed to load:', cfg.modelUrl, err));
}

function boot() { document.querySelectorAll('.jay-hero').forEach(initHero); }
if (document.readyState !== 'loading') boot(); else addEventListener('DOMContentLoaded', boot);
document.addEventListener('shopify:section:load', (e) => {
  const el = e.target.querySelector('.jay-hero'); if (el) { el.dataset.jayInit = ''; initHero(el); }
});
